import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import Experience from "../Experience.js";
import EventEmitter from "./EventEmitter.js";

export default class Resources extends EventEmitter {
  constructor(sources) {
    super();
    this.experience = new Experience();
    this.sources = sources;
    this.items = {};
    this.toLoad = this.sources.length;
    this.loaded = 0;

    this.setLoaders();
    this.startLoading();
  }

  setLoaders() {
    this.loaders = {};
    this.loadingPercentageElement = document.querySelector(
      ".loading-percentage",
    );
    this.loadingGifElement = document.querySelector(".loading-gif");

    this.loadingManager = new THREE.LoadingManager(
      // Loaded
      () => {
        // LoadingManager also calls this after a failed item; keep the error visible
        if (this.failed) return;

        const reveal = () => {
          // Animate overlay (created after Resources, so look it up now)
          this.experience.overlay.animateMaterial();
          this.loadingPercentageElement.remove();
          this.loadingGifElement.remove();
          document.body.classList.add("loaded");
        };

        // The world is built by now ('ready' fired first). Compile its shaders
        // while the overlay still hides the scene so the fade doesn't stutter.
        const { renderer, scene, camera } = this.experience;
        renderer.instance.compile(scene, camera.instance);
        window.setTimeout(reveal, 300);
      },

      (itemUrl, itemsLoaded, itemsTotal) => {
        if (this.failed) return;
        const progressRatio = itemsLoaded / itemsTotal;
        const percentage = Math.floor(progressRatio * 100);
        this.loadingPercentageElement.textContent = `${percentage}%`;
      },

      // Error: otherwise the loader would sit at its last percentage forever
      (url) => {
        this.failed = true;
        console.error(`Failed to load ${url}`);
        this.loadingPercentageElement.textContent =
          "Something didn't load. Please refresh the page.";
      },
    );

    this.loaders.gltfLoader = new GLTFLoader(this.loadingManager);
    this.loaders.textureLoader = new THREE.TextureLoader(this.loadingManager);
    this.loaders.cubeTextureLoader = new THREE.CubeTextureLoader(
      this.loadingManager,
    );
  }

  startLoading() {
    // Load each source
    for (const source of this.sources) {
      if (source.type === "gltfModel") {
        this.loaders.gltfLoader.load(source.path, (file) => {
          this.sourceLoaded(source, file);
        });
      } else if (source.type === "texture") {
        this.loaders.textureLoader.load(source.path, (file) => {
          this.sourceLoaded(source, file);
        });
      } else if (source.type === "cubeTexture") {
        this.loaders.cubeTextureLoader.load(source.path, (file) => {
          this.sourceLoaded(source, file);
        });
      }
    }
  }

  sourceLoaded(source, file) {
    this.items[source.name] = file;

    this.loaded++;

    if (this.loaded === this.toLoad) {
      this.trigger("ready");
    }
  }
}
